import type { FC, ReactElement, ReactNode } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';
import { WarningIcon } from '@polyai/ui/components/atoms/Icons';
import Text from '@polyai/ui/components/atoms/Text';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import useIsOverflowing from '@polyai/ui/hooks/useIsOverflowing';

import TimeAgoTooltip from '../../atoms/TimeAgoTooltip';
import * as Styled from './JupiterCard.styled';

dayjs.extend(relativeTime);

interface Props {
  name: string;
  lastUpdated: string | undefined;
  detail?: string | ReactNode | undefined;
  loading?: boolean;
  warning?: string;
  icon?: React.JSX.Element;
  nameFontSize?: Styled.FontSize;
  warningIcon?: React.JSX.Element;
}

const CardName: FC<Props> = ({
  name,
  lastUpdated,
  detail,
  loading,
  warning,
  icon,
  nameFontSize = 'lg',
  warningIcon = undefined,
  ...props
}) => {
  const { isOverflow, targetRef } = useIsOverflowing();

  return (
    <Styled.NameContainer {...props}>
      <Styled.CardTextContainer>
        {loading ? (
          <Styled.NameSkeletonLoader />
        ) : (
          <Styled.Name>
            {warning && (
              <Tooltip content={warning} portal>
                {warningIcon ?? <WarningIcon customFill />}
              </Tooltip>
            )}
            {icon && icon}

            <ConditionalWrapper
              condition={isOverflow}
              wrapper={children => (
                <Tooltip
                  content={name}
                  placement="top-start"
                  targetOverflow={isOverflow}
                >
                  {children as ReactElement}
                </Tooltip>
              )}
            >
              <Styled.NameText ref={targetRef} $fontSize={nameFontSize}>
                {name}
              </Styled.NameText>
            </ConditionalWrapper>
          </Styled.Name>
        )}
        {loading ? (
          <Styled.EditedSkeletonLoader />
        ) : detail ? (
          typeof detail === 'string' ? (
            <Text color="textHelper" variant="bodySmall">
              {detail}
            </Text>
          ) : (
            detail
          )
        ) : (
          lastUpdated && <TimeAgoTooltip timestamp={lastUpdated} />
        )}
      </Styled.CardTextContainer>
    </Styled.NameContainer>
  );
};

export default CardName;
